import { Row, Card, Col, CardBody, CardTitle } from "reactstrap";
import moment from "moment";
import React, { useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";

// The permit costs its price, untaxed. Tax is charged once when money enters
// the wallet, so spending that balance is not taxed again. Card payments do
// collect price + tax at the gateway, but only because paying by card is a
// top-up and a purchase in one step -- the tax there belongs to the top-up.
function permitPrice(permitType) {
  if (!permitType) return null;
  return parseFloat(permitType.price) || 0;
}

const money = (n) => (n === null || n === undefined ? "-" : `MVR ${Number(n).toFixed(2)}`);

// The next five-minute mark, always at least a minute ahead.
function nextSlot(from = moment()) {
  const t = from.clone().seconds(0).milliseconds(0);
  const over = t.minutes() % 5;
  return t.add(over === 0 ? 5 : 5 - over, "minutes");
}

// The window the picker offers, and what to say about it.
//
// Only a permit that is running *now* stops another starting now. One booked
// for later leaves the vessel free until it begins, and that gap is bookable —
// the picker used to floor selection at the end of the last permit, so a
// customer on the 14th could not book the 15th because of a permit on the 20th.
//
// The earliest selectable moment is therefore always the next slot. Whether a
// particular choice overlaps is the server's rule, not the picker's: it knows
// the permit's duration, which is still being chosen here, and it names the
// date the vessel is free when it refuses.
export function permitDateWindow(vessel, from = moment()) {
  const earliest = nextSlot(from);

  const permits = (vessel?.permits || [])
    .map((p) => ({
      start: moment(p.effective_at ?? p.permit_effective_at),
      end: moment(p.expires_at ?? p.permit_expires_at),
    }))
    .filter((p) => p.end.isValid() && p.end.isAfter(from));

  // A permit with no usable start is treated as running, which errs towards
  // the old behaviour rather than offering a slot that will be refused.
  const running = permits.filter((p) => !p.start.isValid() || !p.start.isAfter(from));
  const upcoming = permits.filter((p) => p.start.isValid() && p.start.isAfter(from));

  const busyUntil = running.length ? moment.max(running.map((p) => p.end)) : null;
  const freeUntil = !busyUntil && upcoming.length ? moment.min(upcoming.map((p) => p.start)) : null;

  return {
    min: earliest,
    defaultStart: busyUntil && busyUntil.isAfter(earliest) ? busyUntil : earliest,
    busyUntil,
    freeUntil,
  };
}

function permitPreviewEndDisplay(durationDays, activationDate) {
  const d = Number(durationDays);
  if (!Number.isFinite(d) || d < 1) return null;
  const base = activationDate ? moment(activationDate, "YYYY-MM-DDTHH:mm") : moment();
  return base.add(d * 24, "hours").format("D MMM YYYY, HH:mm");
}

const ApplyForPermit = ({
  goToVesselDetails,
  handleApplyForPermit,
  selectedVessel,
  selectedPermitType,
  setSelectedPermitType,
  permitTypes,
  myBalance,
  error,
  walletTaxRate = 0,
}) => {
  // Rounded up to the next five minutes: a permit timestamped in the past is
  // immediately partly spent, and the minutes spent filling this form can push
  // "now" behind by the time it submits. The minute itself is also awkward to
  // type back in once the picker has been touched.
  const { min, defaultStart, busyUntil, freeUntil } = permitDateWindow(selectedVessel);
  const minDate = min.format("YYYY-MM-DDTHH:mm");
  const maxDate = moment().add(6, "months").format("YYYY-MM-DDTHH:mm");

  // Where the form opens. Only a permit running now pushes this forward; the
  // floor stays at minDate either way, so an earlier gap is still selectable.
  const [activationDate, setActivationDate] = useState(defaultStart.format("YYYY-MM-DDTHH:mm"));
  const [confirmBalanceDlg, setConfirmBalanceDlg] = useState(false);

  const selectedPermit = permitTypes.find(
    (pt) => pt.id === parseInt(selectedPermitType, 10)
  );
  const price = permitPrice(selectedPermit);
  const gst = price !== null ? Math.round(price * walletTaxRate * 100) / 100 : 0;
  const total = price !== null ? Math.round((price + gst) * 100) / 100 : 0;
  const periodEndLabel = permitPreviewEndDisplay(selectedPermit?.duration, activationDate);
  // Paying from balance costs the price, so that is what must be affordable.
  const hasBalance = myBalance !== null && myBalance !== undefined && price !== null && myBalance >= price;

  const onPay = (method) => {
    handleApplyForPermit({ activationDate, paymentMethod: method });
  };

  return (
    <React.Fragment>
      <button
        className="btn btn-primary fs-6 py-1"
        onClick={() => goToVesselDetails({ vessel: selectedVessel })}
      >
        <span className="mdi mdi-chevron-left align-middle fs-3 me-2"></span>
        <span>Back</span>
      </button>

      <Card className="px-3 py-1 mt-4 mb-3">
        <Col>
          <h4 className="pt-3 font-weight-semibold">Apply for Permit</h4>
        </Col>

        {/* Permit Type */}
        <Row className="mt-2">
          <Col className="col-12">
            <label className="col-form-label">Permit Type (Duration)</label>
          </Col>
          <Col className="col-md-4 col-lg-4 col-xl-3">
            <select
              className="form-control"
              value={selectedPermitType}
              onChange={(e) => setSelectedPermitType(e.target.value)}
            >
              {permitTypes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </Col>
        </Row>

        {/* Activation Date */}
        <Row className="mt-3">
          <Col className="col-12">
            <label className="col-form-label">Activation Date</label>
          </Col>
          <Col className="col-md-4 col-lg-4 col-xl-3">
            <input
              type="datetime-local"
              className="form-control"
              value={activationDate}
              min={minDate}
              max={maxDate}
              onChange={(e) => setActivationDate(e.target.value)}
            />
          </Col>
        </Row>

        {busyUntil && (
          <Row className="mt-2">
            <Col className="col-12">
              <span className="text-muted small">
                This vessel has a permit until {busyUntil.format("D MMM YYYY, HH:mm")}. A new permit
                can start from then.
              </span>
            </Col>
          </Row>
        )}

        {freeUntil && (
          <Row className="mt-2">
            <Col className="col-12">
              <span className="text-muted small">
                This vessel is free until {freeUntil.format("D MMM YYYY, HH:mm")}, when an existing
                permit begins. A permit starting before then has to end before it.
              </span>
            </Col>
          </Row>
        )}

        {/* Period Preview */}
        {periodEndLabel && (
          <Row className="mt-3">
            <Col className="col-12">
              <div className="rounded border bg-light px-3 py-2">
                <span className="text-muted small d-block mb-1">Permit period</span>
                <span className="font-weight-semibold fs-5 text-body">
                  {`Starts ${moment(activationDate, "YYYY-MM-DDTHH:mm").format("D MMM YYYY, HH:mm")}, ends ${periodEndLabel}`}
                </span>
              </div>
            </Col>
          </Row>
        )}

        {/* The permit itself is untaxed. Paying by card tops the wallet up
            first, and GST is charged on that top-up — so the card route shows
            the breakdown while the balance route is simply the price. */}
        {selectedPermit && (
          <Row className="mt-3">
            <Col className="col-md-5 col-lg-5 col-xl-4">
              <div className="rounded border bg-light px-3 py-2">
                <div className="d-flex justify-content-between py-1">
                  <span className="text-muted">Permit price</span>
                  <span>{money(price)}</span>
                </div>
                <div className="d-flex justify-content-between py-1">
                  <span className="text-muted">GST ({(walletTaxRate * 100).toFixed(0)}%)</span>
                  <span>{money(gst)}</span>
                </div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between py-1">
                  <span className="fw-bold">Card payment</span>
                  <span className="fw-bold fs-5 text-primary">{money(total)}</span>
                </div>
                {hasBalance && (
                  <div className="text-muted mt-2" style={{ fontSize: "0.85em" }}>
                    Paying from balance costs {money(price)}.
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )}

        {/* Payment Buttons */}
        <Col>
          <Row className="mt-3 mb-3">
            {hasBalance && (
              <Col className="col-auto">
                <button
                  className="btn btn-success fs-6"
                  onClick={() => setConfirmBalanceDlg(true)}
                >
                  <span className="mdi mdi-wallet align-middle fs-4 me-1"></span>
                  Pay with Balance
                </button>
              </Col>
            )}
            <Col className="col-auto">
              <button
                className="btn btn-primary fs-6"
                onClick={() => onPay("bml")}
              >
                <span className="mdi mdi-credit-card align-middle fs-4 me-1"></span>
                Top up &amp; Pay
              </button>
            </Col>
          </Row>
        </Col>

        {error?.Message && error.Message.length > 0 && (
          <Col>
            <Card color="danger" className="text-white-50">
              <CardBody className="p-3">
                <CardTitle className="mb-2 text-white">
                  <i className="mdi mdi-block-helper me-3" />
                  Error
                </CardTitle>
                <span className="text-white">{error.Message}</span>
              </CardBody>
            </Card>
          </Col>
        )}
      </Card>

      {confirmBalanceDlg && (
        <SweetAlert
          showCancel
          confirmBtnText="Confirm &amp; Pay"
          cancelBtnText="Cancel"
          confirmBtnBsStyle="success"
          cancelBtnBsStyle="secondary"
          title="Confirm Permit Purchase"
          onConfirm={() => {
            setConfirmBalanceDlg(false);
            onPay("balance");
          }}
          onCancel={() => setConfirmBalanceDlg(false)}
        >
          <div className="text-start px-2">
            <div className="d-flex justify-content-between py-1">
              <span className="text-muted">{selectedPermit?.name}</span>
              <span>{money(price)}</span>
            </div>
            <hr className="my-2" />
            <div className="d-flex justify-content-between py-1">
              <span className="fw-bold">Deducted from balance</span>
              <span className="fw-bold fs-5 text-success">{money(price)}</span>
            </div>
            <div className="text-muted mt-3" style={{ fontSize: "0.85em" }}>
              GST will not be applicable for your permit purchase as GST was
              charged for your current Topped-up amount. Valid from{" "}
              {moment(activationDate, "YYYY-MM-DDTHH:mm").format("D MMM YYYY, HH:mm")}.
            </div>
          </div>
        </SweetAlert>
      )}
    </React.Fragment>
  );
};

export default ApplyForPermit;
